import { reportError } from "@lib/utils/reportError"
import { Deferred } from "@lib/utils/deferred"
import { posthog, AnalyticsEvent } from "@src/analytics"
import { LoadedRegion } from "../AudioPlayer"
import { PitchShiftNode } from "../pitch-shift"
import { AudioReader, createAudioRingBuffer, createRingBufferStorage } from "../ring-buffer/audioqueue"
import { RegionInfo } from "../Track"
import { TrackInfo } from "../types"
import { PlaybackBackendDebugState } from "../backends/PlaybackBackend"
import { MixerNode } from "./MixerNode"
import {
  normalizeTrackTimeline,
  StreamingTimelineOverlapError,
} from "./normalizeTrackTimeline"
import {
  bumpTransportGeneration,
  createSharedMixerState,
  isPlaying,
  setMasterGain,
  setPlaying,
  setTrackGain,
  SharedMixerState,
} from "./shared-state"
import { StreamingWorkerClient } from "./StreamingWorkerClient"
import { StreamingWorkerRegion } from "./worker/messages"

const OUTPUT_CHANNELS = 2
const PCM_BUFFER_SECONDS = 2

type ReadyStateListener = (ready: Promise<void>) => void

interface RuntimeState {
  audioContext: AudioContext
  mixerNode: MixerNode
  sharedState: SharedMixerState
  trackBufferCapacities: number[]
  trackBufferReaders: AudioReader[]
  trackIds: ReadonlySet<string>
  workerClient: StreamingWorkerClient
}

interface StreamingRegionState extends RegionInfo {
  audioBuffer?: AudioBuffer
  audioBlob?: Blob
  codec: string | null
  preDecodedPcm?: Float32Array
  trackId: string
}

/**
 * Caches encoded audio data (ArrayBuffers) by region ID so that runtime
 * rebuilds (add/remove track, seek after mutation, etc.) don't re-read the
 * source Blobs.  Each call to `getTransferableData` returns a fresh copy
 * suitable for `postMessage` transfer without invalidating the cache.
 */
class EncodedDataCache {
  private readonly entries = new Map<string, ArrayBuffer>()

  async resolve(region: StreamingRegionState): Promise<ArrayBuffer> {
    const existing = this.entries.get(region.id)
    if (existing) {
      return existing
    }

    if (!region.audioBlob) {
      throw new Error(`Streaming region ${region.id} is missing encoded audio data`)
    }

    const buffer = await region.audioBlob.arrayBuffer()
    this.entries.set(region.id, buffer)
    return buffer
  }

  /**
   * Returns a copy of the cached ArrayBuffer that can be transferred to a
   * worker without detaching the cache's reference.
   */
  getTransferableData(regionId: string): ArrayBuffer {
    const buffer = this.entries.get(regionId)
    if (!buffer) {
      throw new Error(`Streaming region ${regionId} has no cached encoded data`)
    }

    return buffer.slice(0)
  }

  delete(regionId: string): void {
    this.entries.delete(regionId)
  }

  clear(): void {
    this.entries.clear()
  }

  /** Test-only: check whether a region has cached data. */
  has(regionId: string): boolean {
    return this.entries.has(regionId)
  }
}

function getLoadedRegionDuration(region: LoadedRegion): number {
  return region.duration ?? region.audioBuffer?.duration ?? 0
}

function toStreamingRegionState(
  region: LoadedRegion,
  trackId: string = region.trackId
): StreamingRegionState {
  return {
    audioBuffer: region.audioBuffer,
    audioBlob: region.audioBlob,
    codec: region.codec,
    duration: getLoadedRegionDuration(region),
    id: region.id,
    position: region.position,
    preDecodedPcm: region.preDecodedPcm,
    trackId,
  }
}

interface StreamingAudioPlayerLoadOptions {
  audioContext: AudioContext
  regions: LoadedRegion[]
  sampleRate: 48000
  tracks: TrackInfo[]
}

export class StreamingAudioPlayer {
  private audioContext: AudioContext | null = null
  private readonly encodedDataCache = new EncodedDataCache()
  private loadGeneration = 0
  private masterGain = 1
  private playbackRate = 1
  private pitchShiftBypassed = true
  private pitchShiftNode: PitchShiftNode | null = null
  private readonly readyStateListeners = new Set<ReadyStateListener>()
  private readyDeferred = new Deferred<void>()
  private runtime: RuntimeState | null = null
  private tracks: TrackInfo[] = []
  private regions: StreamingRegionState[] = []
  private readonly trackBufferLowWatermarks = new Map<string, number>()
  private readonly reportedTimelineOverlapKeys = new Set<string>()
  private trackGains = new Map<string, number>()
  private transpose = 0

  get ready(): Promise<void> {
    return this.readyDeferred.promise
  }

  async load(options: StreamingAudioPlayerLoadOptions): Promise<void> {
    this.audioContext = options.audioContext
    this.tracks = [...options.tracks]
    this.regions = options.regions.map((region) => toStreamingRegionState(region))

    await this.ensurePitchShiftNode(options.audioContext)
    await this.rebuildRuntime(options.sampleRate)
  }

  onReadyStateChange(listener: ReadyStateListener): () => void {
    this.readyStateListeners.add(listener)
    return () => {
      this.readyStateListeners.delete(listener)
    }
  }

  play(timestamp: number): Promise<void> {
    const runtime = this.requireRuntime()
    setPlaying(runtime.sharedState, true)
    return runtime.workerClient.play(timestamp, this.playbackRate).then(() => {
      this.clearTrackBufferDiagnostics()
    })
  }

  pause(): void {
    if (!this.runtime) {
      return
    }

    setPlaying(this.runtime.sharedState, false)
    this.runtime.workerClient.pause()
  }

  seek(timestamp: number): Promise<void> {
    const runtime = this.requireRuntime()
    bumpTransportGeneration(runtime.sharedState)
    return runtime.workerClient.seek(timestamp, this.playbackRate).then(() => {
      this.clearTrackBufferDiagnostics()
    })
  }

  setMasterGain(gain: number): void {
    this.masterGain = gain
    if (!this.runtime) {
      return
    }

    setMasterGain(this.runtime.sharedState, gain)
  }

  setPlaybackRate(rate: number): void {
    this.playbackRate = rate
    this.runtime?.workerClient.setPlaybackRate(rate)
    this.updatePitchShift()
  }

  setTranspose(semitones: number): void {
    this.transpose = semitones
    this.updatePitchShift()
  }

  setTrackGain(trackId: string, gain: number): void {
    this.trackGains.set(trackId, gain)
    if (!this.runtime) {
      return
    }

    const trackIndex = this.tracks.findIndex((track) => track.id === trackId)
    if (trackIndex >= 0) {
      setTrackGain(this.runtime.sharedState, trackIndex, gain)
    }
  }

  addTracks(options: { tracks: TrackInfo[]; regions: LoadedRegion[] }): void {
    this.tracks = [...this.tracks, ...options.tracks]
    this.regions = [
      ...this.regions,
      ...options.regions.map((region) => toStreamingRegionState(region)),
    ]

    if (options.regions.length === 0) {
      return
    }

    this.queueRebuild()
  }

  addEmptyTrack(id: string): void {
    this.tracks = [
      ...this.tracks,
      { id, label: id, order: this.tracks.length },
    ]
  }

  removeTrack(id: string): void {
    const removedRegions = this.regions.filter((region) => region.trackId === id)
    const hadRegions = removedRegions.length > 0
    this.tracks = this.tracks.filter((track) => track.id !== id)
    this.regions = this.regions.filter((region) => region.trackId !== id)
    this.trackGains.delete(id)

    for (const region of removedRegions) {
      this.encodedDataCache.delete(region.id)
    }

    if (!hadRegions) {
      return
    }

    this.queueRebuild()
  }

  addRegionToTrack(trackId: string, region: LoadedRegion): void {
    const regionState = toStreamingRegionState(region, trackId)
    this.regions = [...this.regions, regionState]

    if (!this.runtime) {
      // No runtime yet — region will be included when initializeRuntime runs.
      return
    }

    if (!this.runtime.trackIds.has(trackId)) {
      // Track was added after the last init (e.g. via addTracks with no regions).
      // The worker has no ring buffer for this track yet, so a full rebuild is
      // required to provision it.
      this.queueRebuild()
      return
    }

    void this.addRegionToRuntime(regionState, this.runtime).catch((error) => {
      reportError(error)
    })
  }

  /**
   * Resolves encoded data for a newly added region and sends an incremental
   * `addRegion` message to the live worker, avoiding a full rebuild.
   *
   * For pre-decoded PCM regions the PCM buffer is transferred directly to the
   * worker (no copy).  The encoded source bytes are cached beforehand so that
   * any subsequent rebuild triggered by a remove can still include this region.
   */
  private async addRegionToRuntime(
    regionState: StreamingRegionState,
    runtime: RuntimeState
  ): Promise<void> {
    await this.encodedDataCache.resolve(regionState)

    // Guard: runtime may have been destroyed while we awaited blob resolution.
    if (this.runtime !== runtime) {
      return
    }

    let workerRegion: StreamingWorkerRegion

    if (regionState.preDecodedPcm) {
      workerRegion = {
        codec: "pcm",
        duration: regionState.duration,
        encodedAudioData: new ArrayBuffer(0),
        id: regionState.id,
        position: regionState.position,
        preDecodedPcm: regionState.preDecodedPcm.buffer as ArrayBuffer,
        trackId: regionState.trackId,
      }
      // Worker now owns the PCM buffer; encoded cache is the rebuild fallback.
      regionState.preDecodedPcm = undefined
      regionState.audioBlob = undefined
    } else {
      if (!regionState.codec) {
        throw new Error(`Streaming region ${regionState.id} is missing codec metadata`)
      }
      workerRegion = {
        codec: regionState.codec,
        duration: regionState.duration,
        encodedAudioData: this.encodedDataCache.getTransferableData(regionState.id),
        id: regionState.id,
        position: regionState.position,
        trackId: regionState.trackId,
      }
      regionState.audioBlob = undefined
    }

    runtime.workerClient.addRegion(workerRegion)
  }

  removeRegionFromTrack(trackId: string, regionId: string): void {
    this.regions = this.regions.filter(
      (region) => !(region.trackId === trackId && region.id === regionId)
    )
    this.encodedDataCache.delete(regionId)
    this.queueRebuild()
  }

  getRegionsInRange(trackId: string, start: number, end: number): RegionInfo[] {
    return this.regions
      .filter((region) => region.trackId === trackId)
      .filter((region) => region.position < end && region.position + region.duration > start)
      .map((region) => ({
        duration: region.duration,
        id: region.id,
        position: region.position,
      }))
  }

  getRegionBuffer(trackId: string, regionId: string): AudioBuffer | undefined {
    return this.regions.find(
      (region) => region.trackId === trackId && region.id === regionId
    )?.audioBuffer
  }

  getDebugState(): PlaybackBackendDebugState | null {
    const runtime = this.runtime
    if (!runtime) {
      return null
    }

    const sampleRate = runtime.audioContext.sampleRate
    const playing = isPlaying(runtime.sharedState)

    return {
      trackBuffers: this.tracks.map((track, index) => {
        const bufferedSamples = runtime.trackBufferReaders[index]?.availableRead() ?? 0
        const bufferedSeconds = bufferedSamples / (sampleRate * OUTPUT_CHANNELS)
        const previousLowWatermark = this.trackBufferLowWatermarks.get(track.id)
        const lowWatermarkSeconds =
          previousLowWatermark !== undefined
            ? Math.min(previousLowWatermark, bufferedSeconds)
            : playing
              ? bufferedSeconds
              : null

        if (lowWatermarkSeconds !== null) {
          this.trackBufferLowWatermarks.set(track.id, lowWatermarkSeconds)
        }

        return {
          bufferedSeconds,
          capacitySeconds: (runtime.trackBufferCapacities[index] ?? 0) / (sampleRate * OUTPUT_CHANNELS),
          lowWatermarkSeconds,
          trackId: track.id,
        }
      }),
    }
  }

  /** Test-only: check whether a region's blob reference has been released. */
  hasRegionBlob(regionId: string): boolean {
    return this.regions.some((r) => r.id === regionId && r.audioBlob !== undefined)
  }

  /** Test-only: check whether encoded data is cached for a region. */
  hasEncodedDataCached(regionId: string): boolean {
    return this.encodedDataCache.has(regionId)
  }

  destroy(): void {
    this.pause()
    this.destroyRuntime()
    this.encodedDataCache.clear()
    this.pitchShiftNode?.destroy()
    this.pitchShiftNode = null
    this.pitchShiftBypassed = true
  }

  private destroyRuntime(): void {
    this.runtime?.workerClient.destroy()
    this.runtime?.mixerNode.disconnect()
    this.clearTrackBufferDiagnostics()
    this.runtime = null
  }

  private clearTrackBufferDiagnostics(): void {
    this.trackBufferLowWatermarks.clear()
  }

  private async ensurePitchShiftNode(audioContext: AudioContext): Promise<void> {
    if (this.pitchShiftNode) {
      return
    }

    this.pitchShiftNode = await PitchShiftNode.create(audioContext)
    this.pitchShiftNode.node.connect(audioContext.destination)
  }

  private notifyReadyStateChange(): void {
    for (const listener of this.readyStateListeners) {
      listener(this.readyDeferred.promise)
    }
  }

  private queueRebuild(): void {
    if (!this.audioContext) {
      throw new Error("StreamingAudioPlayer not loaded")
    }

    const generation = this.loadGeneration + 1

    void this.rebuildRuntime(this.audioContext.sampleRate).catch((error) => {
      if (generation === this.loadGeneration) {
        reportError(error)
      }
    })
  }

  private rebuildRuntime(sampleRate: number): Promise<void> {
    if (!this.audioContext) {
      throw new Error("StreamingAudioPlayer not loaded")
    }

    const generation = ++this.loadGeneration
    this.readyDeferred = new Deferred<void>()
    // Rebuild failures can happen before AudioEngine attaches a listener, but we
    // still want callers awaiting `ready` to observe the rejection instead of
    // leaving the promise pending forever.
    this.readyDeferred.promise.catch(() => {})
    this.notifyReadyStateChange()
    this.destroyRuntime()

    return this.initializeRuntime(this.audioContext, sampleRate, generation).catch((error) => {
      if (generation === this.loadGeneration) {
        this.readyDeferred.reject(error)
      }

      throw error
    })
  }

  private requireRuntime(): RuntimeState {
    if (!this.runtime) {
      throw new Error("StreamingAudioPlayer not loaded")
    }

    return this.runtime
  }

  private applyOutputRouting(): void {
    if (!this.runtime) {
      return
    }

    this.runtime.mixerNode.disconnect()

    if (this.pitchShiftBypassed) {
      this.runtime.mixerNode.connect(this.runtime.audioContext.destination)
      return
    }

    if (!this.pitchShiftNode) {
      throw new Error("Streaming pitch shift not initialized")
    }

    this.runtime.mixerNode.connect(this.pitchShiftNode.node)
  }

  private updatePitchShift(): void {
    if (!this.pitchShiftNode) {
      return
    }

    const rateCorrection = -12 * Math.log2(this.playbackRate)
    const totalSemitones = rateCorrection + this.transpose
    const shouldBypass = Math.abs(totalSemitones) < 0.01

    if (!shouldBypass) {
      this.pitchShiftNode.setSemitones(totalSemitones)
    }

    if (this.pitchShiftBypassed !== shouldBypass) {
      this.pitchShiftBypassed = shouldBypass
      this.applyOutputRouting()
    }
  }

  private reportTimelineOverlap(error: StreamingTimelineOverlapError): void {
    const overlapKey = [
      error.trackId,
      error.previousRegionId,
      error.nextRegionId,
      error.nextRegionStart,
    ].join(":")

    if (this.reportedTimelineOverlapKeys.has(overlapKey)) {
      return
    }

    this.reportedTimelineOverlapKeys.add(overlapKey)
    reportError(error)
    posthog.capture(AnalyticsEvent.AUDIO_STREAMING_TIMELINE_OVERLAP, {
      nextRegionId: error.nextRegionId,
      overlapSeconds: error.overlapSeconds,
      previousRegionId: error.previousRegionId,
      toleranceSeconds: error.toleranceSeconds,
      trackId: error.trackId,
    })
  }

  private async initializeRuntime(
    audioContext: AudioContext,
    sampleRate: number,
    generation: number
  ): Promise<void> {
    const runtimeRegions = normalizeTrackTimeline(this.regions, {
      onOverlap: (error) => this.reportTimelineOverlap(error),
    })

    // Resolve encoded data into the cache (reads Blobs only on first access).
    // For pre-decoded PCM regions that have a source blob, also cache the encoded
    // bytes so that future rebuilds (e.g. triggered by a remove) can still include
    // them via the cache even after the PCM buffer has been transferred to the worker.
    await Promise.all(
      runtimeRegions
        .filter((region) => !region.preDecodedPcm || region.audioBlob)
        .map((region) => this.encodedDataCache.resolve(region))
    )

    // Drop Blob and pre-decoded PCM references from the canonical region list
    // now that the cache owns the source bytes.
    for (const region of this.regions) {
      if (this.encodedDataCache.has(region.id)) {
        region.audioBlob = undefined
        // Clear pre-decoded PCM for regions whose encoded bytes are now cached.
        // The PCM buffer is about to be transferred to the worker; the encoded
        // cache is the rebuild fallback from here on.
        region.preDecodedPcm = undefined
      }
    }

    const workerRegions = runtimeRegions.map((region) => {
      if (region.preDecodedPcm) {
        const hasCachedFallback = this.encodedDataCache.has(region.id)
        return {
          codec: "pcm",
          duration: region.duration,
          encodedAudioData: new ArrayBuffer(0),
          id: region.id,
          position: region.position,
          // Transfer directly when the encoded cache is the rebuild fallback;
          // copy when there is no fallback (no source blob) so the main thread
          // retains the PCM for any future rebuild.
          preDecodedPcm: hasCachedFallback
            ? (region.preDecodedPcm.buffer as ArrayBuffer)
            : (region.preDecodedPcm.buffer as ArrayBuffer).slice(0),
          trackId: region.trackId,
        } satisfies StreamingWorkerRegion
      }

      if (!region.codec) {
        throw new Error(`Streaming region ${region.id} is missing codec metadata`)
      }

      return {
        codec: region.codec,
        duration: region.duration,
        encodedAudioData: this.encodedDataCache.getTransferableData(region.id),
        id: region.id,
        position: region.position,
        trackId: region.trackId,
      } satisfies StreamingWorkerRegion
    })

    const trackBufferCapacities = this.tracks.map(() =>
      Math.ceil(sampleRate * PCM_BUFFER_SECONDS * OUTPUT_CHANNELS)
    )
    const trackBufferSabs = trackBufferCapacities.map((capacity) =>
      createRingBufferStorage(capacity)
    )
    const trackBufferReaders = trackBufferSabs.map((sab) =>
      new AudioReader(createAudioRingBuffer(sab))
    )
    const sharedState = createSharedMixerState(this.tracks.length)
    setMasterGain(sharedState, this.masterGain)

    this.tracks.forEach((track, index) => {
      setTrackGain(sharedState, index, this.trackGains.get(track.id) ?? 1)
    })

    await MixerNode.addModule(audioContext)
    if (generation !== this.loadGeneration) {
      return
    }

    const mixerNode = new MixerNode({
      audioContext,
      sharedStateBuffers: sharedState,
      trackBufferSabs,
    })

    const workerClient = new StreamingWorkerClient({
      audioContext,
      onError: (error) => {
        if (generation !== this.loadGeneration) {
          return
        }

        this.readyDeferred.reject(error)
      },
      onReady: () => {
        if (generation !== this.loadGeneration) {
          return
        }

        this.readyDeferred.resolve()
      },
      regions: workerRegions,
      sharedStateBuffers: sharedState,
      trackBufferSabs,
      tracks: this.tracks.map((track) => ({ id: track.id, order: track.order })),
    })

    if (generation !== this.loadGeneration) {
      workerClient.destroy()
      mixerNode.disconnect()
      return
    }

    this.runtime = {
      audioContext,
      mixerNode,
      sharedState,
      trackBufferCapacities,
      trackBufferReaders,
      trackIds: new Set(this.tracks.map((t) => t.id)),
      workerClient,
    }

    this.applyOutputRouting()
    this.updatePitchShift()

    await this.readyDeferred.promise
  }
}
